/**
 * Decoder for the columnar binary history the Nida server sends to /v1/boats/history-bin (Nida backend/app/colbin.py,
 * docs/105): per boat, a phone layer (sensors) and a PPK layer as typed columns instead of one JSON object per fix.
 *
 * Layout (little-endian): "CBH1" | u32 headerLen | header JSON | column data. The header is padded so the column
 * data starts on an 8-byte boundary, and every column starts on one too, so each is read as a typed array view
 * without copying. Column types: i64d (first value + int32 deltas), lld (1e-8° + int32 deltas, optional presence
 * mask), f32 / f64 (NaN = missing), dict (uint16 index into a string list, 65535 = missing), bool (255 = missing),
 * json (values inline). A missing value is simply left out of the row object, like the JSON endpoint does.
 */

export type ColRow = Record<string, any>

export interface ColContainer {
    from: number
    to: number
    boats: Record<string, Record<string, ColRow[]>>
    errors: Record<string, string>
}

interface Col {
    k: string
    t: 'i64d' | 'lld' | 'f32' | 'f64' | 'dict' | 'bool' | 'json'
    off?: number
    moff?: number
    base?: number
    d?: string[]
    v?: any[]
}

const LL_SCALE = 1e8
const MISSING_DICT = 65535

export function decodeColbin(buf: ArrayBuffer): ColContainer {
    const dv = new DataView(buf)
    if (buf.byteLength < 8 || dv.getUint32(0, false) !== 0x43424831 /* "CBH1" */) throw new Error('not a CBH1 container')
    const hl = dv.getUint32(4, true)
    const hdr = JSON.parse(new TextDecoder().decode(new Uint8Array(buf, 8, hl)))
    const base = 8 + hl
    if (base % 8 !== 0) throw new Error('misaligned CBH1 container')
    const out: ColContainer = { from: hdr.from, to: hdr.to, boats: {}, errors: hdr.errors ?? {} }

    for (const boat of Object.keys(hdr.boats)) {
        out.boats[boat] = {}
        for (const layer of Object.keys(hdr.boats[boat])) {
            const n: number = hdr.boats[boat][layer].n
            const cols: Col[] = hdr.boats[boat][layer].cols
            const values: { k: string; v: ArrayLike<any> }[] = []
            for (const c of cols) values.push({ k: c.k, v: decodeColumn(buf, base, n, c) })
            const rows: ColRow[] = new Array(n)
            for (let i = 0; i < n; i++) {
                const r: ColRow = {}
                for (let j = 0; j < values.length; j++) {
                    const x = values[j].v[i]
                    if (x !== null && x !== undefined) r[values[j].k] = x
                }
                rows[i] = r
            }
            out.boats[boat][layer] = rows
        }
    }
    return out
}

function decodeColumn(buf: ArrayBuffer, base: number, n: number, c: Col): ArrayLike<any> {
    const at = base + (c.off ?? 0)
    switch (c.t) {
        case 'i64d': {
            const d = new Int32Array(buf, at, n)
            const v = new Array<number>(n)
            let cur = c.base as number
            for (let i = 0; i < n; i++) { cur += d[i]; v[i] = cur }
            return v
        }
        case 'lld': {
            const mask = c.moff !== undefined ? new Uint8Array(buf, base + c.moff, n) : null
            let present = n
            if (mask) { present = 0; for (let i = 0; i < n; i++) present += mask[i] }
            const d = new Int32Array(buf, at, present)
            const v = new Array<number | null>(n)
            let cur = c.base as number, j = 0
            for (let i = 0; i < n; i++) {
                if (mask && !mask[i]) { v[i] = null; continue }
                cur += d[j++]
                v[i] = cur / LL_SCALE
            }
            return v
        }
        case 'f32':
        case 'f64': {
            const a = c.t === 'f32' ? new Float32Array(buf, at, n) : new Float64Array(buf, at, n)
            const v = new Array<number | null>(n)
            for (let i = 0; i < n; i++) { const x = a[i]; v[i] = x !== x ? null : x }
            return v
        }
        case 'dict': {
            const a = new Uint16Array(buf, at, n), d = c.d as string[]
            const v = new Array<string | null>(n)
            for (let i = 0; i < n; i++) v[i] = a[i] === MISSING_DICT ? null : d[a[i]]
            return v
        }
        case 'bool': {
            const a = new Uint8Array(buf, at, n)
            const v = new Array<boolean | null>(n)
            for (let i = 0; i < n; i++) v[i] = a[i] === 255 ? null : a[i] === 1
            return v
        }
        default:
            return c.v as any[]
    }
}
